import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import {
  ParameterGroupDetailDto,
  ParameterGroupDto
} from 'src/store/parametergroups/parametergroup';
import {
  createParameterGroup,
  deleteParameterGroup,
  editParameterGroup,
  fetchParameterGroups
} from 'src/store/parametergroups/parametergroups.actions';
import { ParameterGroupApi } from 'src/store/parametergroups/parametergroups.api';
import { selectParameterGroups } from 'src/store/parametergroups/parametergroups.selectors';
import { isLoadingParameterGroups } from 'src/store/requests/requests.selectors';
import { ParameterGroupForm } from './ParameterGroupForm';

export const ParameterGroupsPage = () => {
  const [currentParameterGroup, setCurrentParameterGroup] =
    useState<ParameterGroupDto | null>(null);
  const [parameterGroup, setParameterGroup] =
    useState<Nullable<ParameterGroupDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const parameterGroups = useSelector(selectParameterGroups);
  const loadingParameterGroups = useSelector(isLoadingParameterGroups);

  useEffect(() => {
    dispatch(fetchParameterGroups());
  }, [dispatch]);

  useEffect(() => {
    if (currentParameterGroup) {
      setShowDeleteModal(true);
    }
  }, [currentParameterGroup]);

  const onCancel = () => {
    setCurrentParameterGroup(null);
    setParameterGroup(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (parameterGroup: ParameterGroupDto) => {
    const response = await ParameterGroupApi.getById(parameterGroup.id);
    if (response.ok) {
      setParameterGroup(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (parameterGroup: ParameterGroupDto) => {
    setCurrentParameterGroup(parameterGroup);
  };

  const onConfirmDelete = () => {
    if (currentParameterGroup) {
      dispatch(
        deleteParameterGroup(currentParameterGroup.id, currentParameterGroup)
      );

      setCurrentParameterGroup(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (parameterGroup: ParameterGroupDto) => {
    if (parameterGroup.id) {
      await dispatch(editParameterGroup(parameterGroup.id, parameterGroup));
      onCancel();
    } else {
      await dispatch(createParameterGroup(parameterGroup));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('parameterGroup.plural')}</Header1>
          <BodyType>
            {`${t('crudtext')} ${t('parameterGroup.plural')}`}
          </BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('parameterGroup.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={parameterGroups}
        loading={loadingParameterGroups}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell
              count={parameterGroups?.length}
              label={t('parameterGroup.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${parameterGroup ? t('edit') : t('create.verb')} ${t(
          'parameterGroup.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <ParameterGroupForm
          parameterGroup={parameterGroup}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('parameterGroup.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('parameterGroup.delete')} <q>{currentParameterGroup?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
